import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Tabs } from '@liveauctioneers/hammer-ui-core/tabs';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import CompleteAccountBanner from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import { PaginationFilter } from '@/components/Pagination/hammer-pagination/types/PaginationFilter';
import { RouteButton } from '@/components/RouteButton/RouteButton';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    fetchSavedSearchesIfNeeded,
    fetchSavedSearchItemsIfNeeded,
    getSavedSearchesRecentFilters,
    getSavedSearchFilters,
    onSavedSearchFilterChange,
    onSavedSearchRecentFilterChange,
} from '@/redux/modules/savedSearch';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { getFollowedSearches, getFollowedSellersForYouUrl, getForYouUrl } from '@/utils/urls';

import FollowedSellersPageContainer from '../../FollowedSellers/FollowedSellersPageContainer';
import ForYouPageHeaderMeta from '../ForYouPageHeaderMeta/ForYouPageHeaderMeta';
import ManageSavedSearches from './components/MySavedSearch/ManageSavedSearches';
import MySavedSearchPageRecentItems from './components/MySavedSearch/MySavedSearchPageRecentItems';
import PageTitle from './components/PageTitle/PageTitle';

export type ForYouPageProps = {
    baseUrl: string;
    page: number;
    pathname: string;
    pushNewUrl: (url: string) => void;
    sort: string;
    tab: string;
    terms: string;
};

const ForYouPage = ({ baseUrl, page, pathname, pushNewUrl, tab, terms }: ForYouPageProps) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const whiteLabel = useAppSelector(getIsWhiteLabel);
    const isMobileView = useIsNarrowerThan(769);
    const [hasLoaded, setHasLoaded] = useState(false);

    const searchItemsFilters = useAppSelector(getSavedSearchesRecentFilters);
    const searchFilters = useAppSelector(getSavedSearchFilters);

    const onSearchItemsFilterChange = (searchItemsFilters: PaginationFilter) => {
        dispatch(onSavedSearchRecentFilterChange(searchItemsFilters));
    };

    const onSearchesFilterChange = (searchFilters: PaginationFilter) => {
        dispatch(onSavedSearchFilterChange(searchFilters));
    };

    useEffect(() => {
        if (tab === 'for-you') {
            dispatch(fetchSavedSearchItemsIfNeeded(true));
            if (!hasLoaded) {
                setHasLoaded(true);
            }
        }
    }, [dispatch, tab, page, searchItemsFilters.pageSize, searchItemsFilters.sort, terms, hasLoaded]);

    useEffect(() => {
        if (tab === 'followed-searches') {
            dispatch(
                fetchSavedSearchesIfNeeded(
                    true,
                    page,
                    Number(searchFilters.pageSize),
                    searchFilters.sort,
                    searchFilters.keyword
                )
            );
        }
    }, [dispatch, tab, page, searchFilters.keyword, searchFilters.pageSize, searchFilters.sort, terms]);

    const onTabChange = React.useCallback(
        (name: string) => {
            if (name === 'for-you') {
                pushNewUrl(getForYouUrl());
                dispatch(fetchSavedSearchItemsIfNeeded(true));
            } else if (name === 'followed-auctioneers-for-you') {
                pushNewUrl(getFollowedSellersForYouUrl());
            } else if (name === 'followed-searches') {
                pushNewUrl(getFollowedSearches());
                dispatch(fetchSavedSearchesIfNeeded(true));
            }
        },
        [dispatch, pushNewUrl]
    );

    const titleCallToAction =
        tab === 'followed-searches' && !isMobileView ? (
            <FollowMoreCategoriesLink
                aria-label="Follow More Categories"
                size="sm"
                to="/c/"
            >
                {formatMessage({ id: 'forYou.followMoreCategories' })}
            </FollowMoreCategoriesLink>
        ) : null;

    return (
        <StyledForYouPage>
            <ForYouPageHeaderMeta
                baseUrl={baseUrl}
                tab={tab}
            />
            <CompleteAccountBanner />
            <StyledPageTitle
                callToAction={titleCallToAction}
                title={<FormattedMessage id="forYou.title" />}
            />
            {!whiteLabel ? (
                <Tabs
                    currentTab={pathname && pathname.split('/')[2]}
                    onTabChanged={onTabChange}
                    tabs={[
                        {
                            content: (
                                <MySavedSearchPageRecentItems
                                    filters={{
                                        ...searchItemsFilters,
                                        page: page || 1,
                                        tab: 'items',
                                    }}
                                    onSavedSearchRecentFilterChange={onSearchItemsFilterChange}
                                />
                            ),
                            contentId: 'for-you',
                            label: <FormattedMessage id="forYou.items" />,
                        },
                        {
                            content: (
                                <ManageSavedSearches
                                    filters={{
                                        ...searchFilters,
                                        page: page,
                                        tab: 'searches',
                                    }}
                                    onSavedSearchRecentFilterChange={onSearchesFilterChange}
                                />
                            ),
                            contentId: 'followed-searches',
                            label: <FormattedMessage id="forYou.searches" />,
                        },
                        {
                            content: <FollowedSellersPageContainer tab={tab} />,
                            contentId: 'followed-auctioneers-for-you',
                            label: <FormattedMessage id="forYou.auctioneers" />,
                        },
                    ]}
                />
            ) : null}
        </StyledForYouPage>
    );
};

export default ForYouPage;

const StyledForYouPage = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 16px;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    align-self: center;
    width: 100%;
    position: relative;
    margin: 0 auto 128px;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobileWidth}) {
        && {
            margin: 0 0 64px;
        }
    }
`;

const StyledPageTitle = styled(PageTitle)`
    && {
        margin: 32px 0;
    }
`;

const FollowMoreCategoriesLink = styled(RouteButton)`
    margin-top: 5px;
`;
